/**
 * The composer: sequences the reader writes, encoded and placed on the map (`V137`, P29.5, P29.6).
 *
 * **A renderer with no state of its own beyond the DOM and the half-finished sequence.**
 * `panels/trajectory.ts` is the pattern: the pane owns the records and hands this a `View`, this
 * turns it into controls and calls back. The one thing held here is the chip list being composed,
 * because it is not a record until it is placed and `main.ts` has no use for a half-typed peptide.
 *
 * ## Two ways in, as asked
 *
 * | mode | for | what it produces |
 * |---|---|---|
 * | `compose` | one peptide, built residue by residue | one `place`, one `/encode` |
 * | `paste` | a CSV or a list from a spreadsheet | one `import`, N `/encode` in order |
 *
 * ## Chips rather than a text field with completion over it
 *
 * A chip is a residue **position**, which is what the diff in `trajectory.ts` counts — that
 * comparison is position by position on the hyphenated codes, so a control whose unit is the
 * position keeps the two agreeing. The rejected alternative was one field holding the whole
 * hyphenated string with completion on the code under the caret: the app would then have to decide
 * where a code begins and ends inside a string being mid-edited, every completion would be a
 * splice with the caret restored, and "is this valid" would have a time-dependent answer.
 *
 * Pasting into the field still works: a paste containing `-` is expanded into chips, so the
 * gesture someone actually has — a sequence on the clipboard — needs no mode change.
 *
 * ## The completion list is a convenience and never a ceiling (`V141`)
 *
 * It comes from `GET /residues`, the **depiction library**, which is a different artefact from the
 * checkpoint's alphabet — see `api.residueCodes` for the measurement and why the two agree today.
 * So **an unlisted code can still be typed and submitted.** It is chipped with a dashed border
 * meaning *no structure to show for this one*, and the server answers either a coordinate or a 400
 * naming it. That is what makes building on the wrong-but-currently-identical set safe: the
 * mismatch can cost a completion, never the ability to place a peptide.
 *
 * ## Length warns and does not refuse (`V141`)
 *
 * **No encoder in this repo declares a maximum** — `models/base.py`'s `max_input_length` is `None`
 * and nothing overrides it — so `manifest.max_length` is the *data pipeline's* truncation length,
 * not a bound the server would enforce. Refusing on it would be greying out something valid, which
 * is the inverse of `validFills`. So past it the count turns amber and says the sequence is longer
 * than this model was trained on, and `place` stays live.
 */
import type { EncodedMark } from '../encoded';
import { codesOf, completionsFor, joinCodes, parsePaste, type ParsedRow } from '../sequences';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/** How many completions are listed before the rest become a count. */
export const MAX_COMPLETIONS = 12;

export type EncoderTab = 'compose' | 'paste';

/** One row of a paste, once the caller has decided what will happen to it. */
export interface ImportProgress {
  /** How many of `total` have been placed so far. */
  readonly done: number;
  readonly total: number;
  /**
   * Whether the current request is the first against this projection.
   *
   * **Its own flag because the first one can take half a minute** and nothing else explains why:
   * `/encode` loads the projection's reducer on first use, measured at 30.7 s for a 92 MB
   * `umap.joblib`. A progress line reading `placing 1 of 74` for that long reads as a hang.
   */
  readonly loadingProjection: boolean;
}

export interface EncoderHandlers {
  /** Encode one composed sequence and put it on the map. */
  onPlace(sequence: string, label: string): void;
  /** Encode and place every row of a validated paste, in order. */
  onImport(rows: readonly ParsedRow[]): void;
  /** Stop an import before its next request. What has landed stays. */
  onCancel(): void;
  /** Re-encode the records whose build no longer matches, so they can be drawn again. */
  onRePlace(): void;
  /** Open one placed point in the detail pane. */
  onSelect(id: string): void;
  /** Forget one placed point everywhere. */
  onRemove(id: string): void;
}

export interface EncoderView {
  /** Every residue code the library can draw, for completion. Empty when it has none. */
  readonly codes: readonly string[];
  /** `manifest.max_length` — a warning threshold, not a bound. See the module docstring. */
  readonly trainedLength: number;
  /** The points already placed on the view this pane is showing. */
  readonly placed: readonly EncodedMark[];
  /** Records for this atlas and projection whose build no longer matches (`V139`). */
  readonly stale: readonly EncodedMark[];
  /** How many more this view will accept before `add` refuses. */
  readonly freeSlots: number;
  /** Non-null while an import or a re-place is running. */
  readonly progress: ImportProgress | null;
}

export interface EncoderPanel {
  show(view: EncoderView, handlers: EncoderHandlers): void;
  clear(): void;
}

/**
 * The panel.
 *
 * `key` suffixes every id, as every other per-pane panel does (P12.1), so pane A's ids are exactly
 * what they were before side-by-side existed.
 */
export function createEncoderPanel(root: HTMLElement, key: '' | '-b' = ''): EncoderPanel {
  const id = (name: string): string => `${name}${key}`;

  // -- the state that is genuinely this panel's ------------------------------------------------
  //
  // A half-composed peptide is not a record and `main.ts` has no use for one. It is kept across
  // re-renders — which happen on every placement, every progress tick and every view change —
  // because losing four typed chips to a progress update would be the panel's own bug.
  let tab: EncoderTab = 'compose';
  let chips: string[] = [];
  let typed = '';
  let name = '';
  let pasted = '';
  /** Which completion the keyboard is on. `-1` is "none", which is where every keystroke resets it. */
  let active = -1;
  /** Set once a paste has been read, so the report and the import button survive a re-render. */
  let report: ReturnType<typeof parsePaste> | null = null;

  let view: EncoderView | null = null;
  let handlers: EncoderHandlers | null = null;

  const isCode = (code: string): boolean => new Set(view?.codes ?? []).has(code);

  function render(): void {
    if (view === null || handlers === null) return;
    const v = view;
    const h = handlers;

    const tabs = el('div', { class: 'tabs' });
    for (const [value, label] of [
      ['compose', 'compose'],
      ['paste', 'paste'],
    ] as [EncoderTab, string][]) {
      const button = el(
        'button',
        { type: 'button', class: value === tab ? 'selected' : '', id: id(`encoder-tab-${value}`) },
        label,
      );
      button.addEventListener('click', () => {
        tab = value;
        render();
      });
      tabs.append(button);
    }

    const body = tab === 'compose' ? composeBody(v, h) : pasteBody(v, h);
    const running = v.progress !== null;

    root.replaceChildren(
      el(
        'section',
        { class: 'card encoder' },
        el('h2', {}, `encode sequences${v.placed.length === 0 ? '' : ` · ${v.placed.length}`}`),
        tabs,
        // **The placed list is not drawn while an import runs**, and that is a cost decision
        // rather than a tidiness one. This panel re-renders once per placed row, so a 200-row
        // import would rebuild a list growing to 200 entries 200 times — on the order of 100k
        // elements created and discarded across the ten seconds the reader spends watching a
        // progress line. The list is stale mid-import anyway, and the final render draws it whole.
        ...(running ? [progressLine(v.progress as ImportProgress, h)] : [body, ...placedList(v, h)]),
      ),
    );
  }

  function progressLine(progress: ImportProgress, h: EncoderHandlers): HTMLElement {
    const cancel = el('button', { type: 'button' }, 'cancel');
    cancel.addEventListener('click', () => h.onCancel());
    return el(
      'p',
      { class: 'progress' },
      progress.loadingProjection
        ? 'loading the projection — the first encode on a new projection reads its reducer from disk, which can take half a minute'
        : `placing ${progress.done + 1} of ${progress.total}`,
      ' ',
      cancel,
    );
  }

  // -- compose --------------------------------------------------------------------------------

  function composeBody(v: EncoderView, h: EncoderHandlers): HTMLElement {
    const list = el('ul', { class: 'chips' });
    chips.forEach((code, at) => {
      const remove = el('button', { type: 'button', class: 'remove', title: 'remove' }, '×');
      remove.addEventListener('click', () => {
        chips = [...chips.slice(0, at), ...chips.slice(at + 1)];
        render();
      });
      const left = el('button', { type: 'button', class: 'move', title: 'move left' }, '‹');
      left.disabled = at === 0;
      left.addEventListener('click', () => {
        chips = swap(chips, at, at - 1);
        render();
      });
      const right = el('button', { type: 'button', class: 'move', title: 'move right' }, '›');
      right.disabled = at === chips.length - 1;
      right.addEventListener('click', () => {
        chips = swap(chips, at, at + 1);
        render();
      });
      const chip = el('li', { class: isCode(code) ? '' : 'unlisted' });
      // **`data-residue` by hand rather than through `residueSpans`**, and it is the one sequence
      // surface in the app that does. `residueSpans` renders a whole sequence as one `<p>` of
      // spans joined by hyphens; a chip is a `<li>` with its own reorder and delete controls
      // beside the code, so there is no run of text for that helper to produce. What the chip
      // must keep is the *attribute*: the delegated structure card (`P18.1`, `P18.2`) is what
      // answers "what is X0067" here, and it is why the completion list needs no residue names.
      const text = el('span', { class: 'residue' }, code);
      if (isCode(code)) {
        text.dataset['residue'] = code;
        text.tabIndex = 0;
      } else {
        text.title = 'not in the depiction library — it may still be in this model’s alphabet';
      }
      chip.append(text, left, right, remove);
      list.append(chip);
    });

    const field = el('input', {
      type: 'text',
      id: id('encoder-code'),
      placeholder: chips.length === 0 ? 'type a residue code, or paste a whole sequence' : 'next residue',
      autocomplete: 'off',
    });
    field.value = typed;

    /**
     * The two nodes a keystroke changes, held so that typing does **not** rebuild the card.
     *
     * **This is what keeps the text field alive across a keystroke, and that is a correctness
     * property as much as a cost one.** A full `render()` per character replaces the `<input>`
     * with a new element, so focus and the caret have to be found and restored by hand afterwards
     * — a hack that works until something else in the card takes focus first. It is also
     * unbounded work: the card holds the placed list, which grows to `CAP_PER_VIEW`, so every
     * character typed would rebuild up to 200 entries.
     *
     * So a keystroke patches these two in place. Only a change to the **chips** — a commit, a
     * paste expansion, a delete — is a structural change, and only that calls `render()`.
     */
    const completions = el('ul', { class: 'completions' });
    const count = el('p', { id: id('encoder-count') });
    let shown: string[] = [];

    const commit = (code: string): void => {
      const trimmed = code.trim();
      if (trimmed === '') return;
      chips = [...chips, trimmed];
      typed = '';
      active = -1;
      render();
      (document.getElementById(id('encoder-code')) as HTMLInputElement | null)?.focus();
    };

    /** Redraw the completion list and the residue count from `typed` and `chips`, in place. */
    const refresh = (): void => {
      const answer = completionsFor(typed, v.codes, MAX_COMPLETIONS);
      shown = answer.shown;
      const options: HTMLElement[] = shown.map((code, at) => {
        const option = el('li', { class: at === active ? 'active' : '' }, code);
        // `mousedown`, not `click`: the field's blur fires first on a click and would move the
        // list out from under the pointer.
        option.addEventListener('mousedown', (event) => {
          event.preventDefault();
          commit(code);
        });
        return option;
      });
      if (answer.total > shown.length) {
        options.push(el('li', { class: 'more' }, `…and ${answer.total - shown.length} more`));
      }
      completions.replaceChildren(...options);
      completions.hidden = options.length === 0;

      const long = chips.length > v.trainedLength;
      count.className = long ? 'count warn' : 'count';
      count.textContent = long
        ? `${chips.length} residues — longer than the ${v.trainedLength} this model was trained on; it will still be placed`
        : `${chips.length} residues`;
    };

    field.addEventListener('input', () => {
      typed = field.value;
      // A pasted sequence is expanded rather than treated as one code. This is the whole of
      // "pasting into the composer works": the gesture someone has is a sequence on the clipboard.
      // It changes the chips, so it is the one input that redraws the card.
      if (typed.includes('-')) {
        const expanded = codesOf(typed);
        if (expanded.length > 0) {
          chips = [...chips, ...expanded];
          typed = '';
          active = -1;
          render();
          (document.getElementById(id('encoder-code')) as HTMLInputElement | null)?.focus();
          return;
        }
      }
      active = -1;
      refresh();
    });

    field.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && shown.length > 0) {
        event.preventDefault();
        active = (active + 1) % shown.length;
        refresh();
      } else if (event.key === 'ArrowUp' && shown.length > 0) {
        event.preventDefault();
        active = (active <= 0 ? shown.length : active) - 1;
        refresh();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        // The highlighted completion when there is one, otherwise exactly what was typed — which
        // is how an unlisted code gets in.
        commit(active >= 0 ? (shown[active] as string) : typed);
      } else if (event.key === 'Backspace' && typed === '' && chips.length > 0) {
        // The one gesture that makes a chip list feel like a text field.
        chips = chips.slice(0, -1);
        render();
        (document.getElementById(id('encoder-code')) as HTMLInputElement | null)?.focus();
      }
    });

    refresh();

    const nameField = el('input', { type: 'text', id: id('encoder-name'), placeholder: 'optional' });
    nameField.value = name;
    nameField.addEventListener('input', () => {
      name = nameField.value;
    });

    const place = el('button', { type: 'button', class: 'place', id: id('encoder-place') }, 'place on the map');
    place.disabled = chips.length === 0 || v.freeSlots === 0;
    place.addEventListener('click', () => {
      h.onPlace(joinCodes(chips), name.trim());
      chips = [];
      name = '';
      typed = '';
      render();
    });

    const actions = el('div', { class: 'actions' }, place);
    if (v.freeSlots === 0) {
      actions.append(el('span', { class: 'count' }, 'this view is full — remove a point to add another'));
    }

    return el(
      'div',
      {},
      list,
      field,
      // Always in the tree and `hidden` when empty, so `refresh` can fill it without the card
      // being rebuilt to make room for it.
      completions,
      count,
      el('label', { class: 'name', for: id('encoder-name') }, 'name ', nameField),
      actions,
    );
  }

  // -- paste ----------------------------------------------------------------------------------

  function pasteBody(v: EncoderView, h: EncoderHandlers): HTMLElement {
    const box = el('textarea', {
      id: id('encoder-paste'),
      placeholder: 'one sequence per line, or CSV with a sequence column and an optional name',
    });
    box.value = pasted;
    box.addEventListener('input', () => {
      pasted = box.value;
      // The report is only ever the answer to a paste the reader asked about; a keystroke
      // invalidates it rather than silently describing an older text.
      report = null;
    });

    const check = el('button', { type: 'button', id: id('encoder-check') }, 'check');
    check.addEventListener('click', () => {
      report = parsePaste(pasted, isCode);
      render();
    });

    const actions = el('div', { class: 'actions' }, check);
    const parts: (Node | string)[] = [box, actions];

    if (report !== null) {
      const r = report;
      const unlisted = new Map<string, number[]>();
      for (const row of r.rows) {
        for (const code of codesOf(row.sequence)) {
          if (!isCode(code)) {
            unlisted.set(code, [...(unlisted.get(code) ?? []), row.line]);
          }
        }
      }
      const tooMany = r.rows.length > v.freeSlots;
      const notes: (Node | string)[] = [
        el(
          'p',
          {},
          `${r.rows.length} sequence${r.rows.length === 1 ? '' : 's'} read` +
            (r.header ? ', after a header row' : ''),
        ),
      ];
      if (r.ignoredColumns.length > 0) {
        notes.push(el('p', {}, `ignored column${r.ignoredColumns.length === 1 ? '' : 's'}: ${r.ignoredColumns.join(', ')}`));
      }
      if (r.empty.length > 0) {
        notes.push(el('p', { class: 'bad' }, `no residue codes on line${r.empty.length === 1 ? '' : 's'} ${r.empty.join(', ')}`));
      }
      if (unlisted.size > 0) {
        // **Information, not a refusal.** These codes are absent from the depiction library, which
        // is not the model's alphabet — see the module docstring.
        notes.push(
          el(
            'p',
            {},
            `${unlisted.size} code${unlisted.size === 1 ? '' : 's'} not in the depiction library — ` +
              'they may still be in this model’s alphabet, and the server decides: ' +
              [...unlisted.keys()].slice(0, 6).join(', '),
          ),
        );
      }
      if (tooMany) {
        // Refused **whole**. Half an import is a set nobody can tell from a complete one.
        notes.push(
          el(
            'p',
            { class: 'bad' },
            `this view has ${v.freeSlots} slot${v.freeSlots === 1 ? '' : 's'} free and this paste ` +
              `has ${r.rows.length} rows — nothing was placed`,
          ),
        );
      }
      const run = el('button', { type: 'button', class: 'place', id: id('encoder-import') }, `place ${r.rows.length}`);
      run.disabled = r.rows.length === 0 || tooMany;
      run.addEventListener('click', () => {
        h.onImport(r.rows);
        pasted = '';
        report = null;
      });
      actions.append(run);
      parts.push(el('div', { class: 'report' }, ...notes));
    }
    return el('div', {}, ...parts);
  }

  // -- what is already on the map ---------------------------------------------------------------

  function placedList(v: EncoderView, h: EncoderHandlers): HTMLElement[] {
    if (v.placed.length === 0 && v.stale.length === 0) return [];
    const list = el('ol', { class: 'placed' });
    /** The reader's name for it, or the first few residues — enough to tell entries apart. */
    const nameOf = (mark: EncodedMark): string => {
      if (mark.label !== '') return mark.label;
      const codes = codesOf(mark.sequence);
      return joinCodes(codes.slice(0, 4)) + (codes.length > 4 ? '…' : '');
    };
    const entry = (mark: EncodedMark, stale: boolean): HTMLElement => {
      const what = el(
        'button',
        { type: 'button', class: 'what', title: 'show this point in the pane' },
        nameOf(mark),
      );
      what.addEventListener('click', () => h.onSelect(mark.id));
      const remove = el('button', { type: 'button', class: 'remove', title: 'forget this point' }, '×');
      remove.addEventListener('click', () => h.onRemove(mark.id));
      return el(
        'li',
        { class: stale ? 'stale' : '' },
        el(
          'p',
          { class: 'head' },
          what,
          el(
            'span',
            { class: 'where' },
            // A stale record's coordinate is deliberately not shown: it names a position in an
            // embedding that no longer exists, and printing it would invite the reader to compare
            // it with one that does.
            stale ? 'not on this build' : `(${mark.xy[0].toFixed(2)}, ${mark.xy[1].toFixed(2)})`,
          ),
          remove,
        ),
      );
    };
    for (const mark of v.placed) list.append(entry(mark, false));
    for (const mark of v.stale) list.append(entry(mark, true));

    const out: HTMLElement[] = [list];
    if (v.stale.length > 0 && v.progress === null) {
      const again = el(
        'button',
        { type: 'button', id: id('encoder-replace') },
        `re-place ${v.stale.length} point${v.stale.length === 1 ? '' : 's'} on this build`,
      );
      again.addEventListener('click', () => h.onRePlace());
      out.push(
        el(
          'div',
          { class: 'stale-actions' },
          el(
            'p',
            { class: 'count' },
            'this atlas was rebuilt since these were placed, so their coordinates are from an ' +
              'embedding that no longer exists. The sequences are kept and can be encoded again.',
          ),
          again,
        ),
      );
    }
    return out;
  }

  return {
    show(next, nextHandlers) {
      view = next;
      handlers = nextHandlers;
      render();
    },
    clear() {
      view = null;
      handlers = null;
      report = null;
      root.replaceChildren();
    },
  };
}

const swap = <T,>(list: readonly T[], a: number, b: number): T[] => {
  const next = [...list];
  [next[a], next[b]] = [next[b] as T, next[a] as T];
  return next;
};

/** Re-exported so `main.ts` and the tests name one type for a parsed row. */
export type { ParsedRow };
